import React, {useCallback, useEffect, useRef, useState} from 'react';
import PropTypes from "prop-types";
import {connect} from 'react-redux';
import {bindActionCreators} from 'redux';
import ActionCreator from "../../store/actions/index";
import MarkerClusterGroup from 'react-leaflet-markercluster';
import 'leaflet/dist/leaflet.css';
import 'react-leaflet-markercluster/dist/styles.min.css';
import Leaflet from 'leaflet';
import "./map.css";
import Polylines from "./Map/Polylines";
import RefuelMarker from "./Map/RefuelMarker";
import WarningMarker from "./Map/WarningMarker";
import ZonePolyline from "./Map/ZonePolyline";
import Markers from "./Map/Markers";
import Legend from "./Map/Legend";
import Control from "react-leaflet-control";
import MapComponent, {defaultCenter} from "./MapComponent";
import SelectedRoutesSummary from './Map/SelectedRoutesSummary';
import {useQuery} from '../../utils/helpers';
import CustomPin from './Map/CustomPin';
import {withStyles} from "@material-ui/core";
import {compose} from "recompose";
import PoiMarkers from "./Map/PoiMarkers";
import CenterButton from "./Map/CenterButton";

const getClusterSize = (cluster) => {
    const count = cluster.getChildCount();
    let size = 'LargeXL';

    if (count < 5) {
        size = 'Small';
    } else if (count >= 5 && count < 25) {
        size = 'Medium';
    } else if (count >= 25 && count < 100) {
        size = 'Large';
    }
    return {count, size};
};

const createCustomCluster = (cluster) => {
    const c = getClusterSize(cluster);
    return Leaflet.divIcon({
        html: `<span class="markerClusterLabel markerCluster${c.size}">${c.count}</span>`,
        className: `markerClusterLabel, markerCluster${c.size}`
    });
};

const createClusterPoi = function (cluster) {
    const c = getClusterSize(cluster);
    return Leaflet.divIcon({
        html: `<span class="markerClusterLabel markerClusterPoi markerCluster${c.size}">${c.count}</span>`,
        className: `markerClusterLabel, markerClusterPoi, markerCluster${c.size}`
    });
};

const useStyles = () => ({
    map: {
        height: "calc(100vh - 150px)",
    },
});

const OpenStreetMap = (props) => {
    const {
        classes,
        selectedCar,
        cars,
        selectedRoutes,
        showCurrentRoute,
        currentRoute,
        onZoomChange,
        onCenterChange,
        showPoiOnMap,
        mapManuallyMoved,
    } = props;
    const map = useRef(null);
    const isProgrammaticMove = useRef(false);
    const [customCoords, setCustomCoords] = useState(null);
    const query = useQuery();
    const params = {
        id: query.get("id"),
        lat: query.get("lat"),
        lon: query.get("lon")
    };

    const fitCarsInBounds = (markers) => {
        let coords = [];
        if (!markers || markers.length === 0)
            return;
        markers.map(marker => {
            if (!marker.pozycja_gps)
                return false;
            return coords.push([marker.pozycja_gps.lat, marker.pozycja_gps.lon]);
        });
        fitAnyBounds(coords);
    };

    const fitAnyBounds = (coords) => {
        if (!Array.isArray(coords))
            return;
        let bounds = Leaflet.latLngBounds(coords);
        let mapElement = map.current.leafletElement;
        isProgrammaticMove.current = true;
        mapElement.fitBounds(bounds, {padding: [20, 20], maxZoom: 18});
    };

    const onManuallyMovedOrZoomed = () => {
        props.manuallyMovedOrZoomed(true);
    };

    const onUserZoomEnd = () => {
        if (isProgrammaticMove.current) {
            isProgrammaticMove.current = false;
            return;
        }
        props.manuallyMovedOrZoomed(true);
    };

    const bounds = useCallback(
        fitCarsInBounds,
        [cars]
    );

    useEffect(() => {
        //passes fitBounds method to redux store / used for tracking all cars mode
        props.passFitBoundsCallback(bounds);
        props.passFitAnyBoundCallback(fitAnyBounds);
        return () => {
            props.clearRoutes();
            props.clearSelectedCar();
        };
    }, []);

    useEffect(() => {
        if (Object.values(params).every(x => x !== null && x !== "")) {
            setCustomCoords({lat: params.lat, lon: params.lon});
        }
    }, []);

    useEffect(() => {
        props.fitInBounds();
    }, [selectedRoutes]);

    useEffect(() => {
        if (showCurrentRoute && !mapManuallyMoved) {
            props.fitInBounds();
        }
    }, [selectedRoutes, showCurrentRoute, currentRoute]);

    useEffect(() => {
        if (!mapManuallyMoved) {
            if (selectedCar && selectedCar.pozycja_gps && currentRoute === null) {
                isProgrammaticMove.current = true;
                map.current.leafletElement.setView(selectedCar.pozycja_gps, 15);
            } else if (cars.length > 0) {
                bounds(cars);
            } else {
                isProgrammaticMove.current = true;
                map.current.leafletElement.setView(defaultCenter, 7);
            }
            props.fitInBounds();
        }

    }, [selectedCar, cars, bounds]);

    return (
        <div style={{marginRight: 8, position: 'relative'}}>
            <SelectedRoutesSummary/>
            <MapComponent
                ref={map}
                className={classes.map}
                onZoomChange={onZoomChange}
                onCenterChange={onCenterChange}
                onDblClickChange={onManuallyMovedOrZoomed}
                onDragChange={onManuallyMovedOrZoomed}
                onZoomEnd={onUserZoomEnd}
            >
                <Control position="bottomright">
                    <CenterButton/>
                    <Legend/>
                </Control>

                <MarkerClusterGroup
                    showCoverageOnHover={true}
                    iconCreateFunction={createCustomCluster}
                    spiderfyDistanceMultiplier={1.6}
                >
                    <Markers/>
                </MarkerClusterGroup>
                <MarkerClusterGroup
                    iconCreateFunction={createClusterPoi}
                    spiderfyDistanceMultiplier={1.6}
                >
                    {showPoiOnMap ? <PoiMarkers/> : <></>}
                </MarkerClusterGroup>
                {(customCoords && selectedCar) && <CustomPin position={customCoords}/>}
                <Polylines/>
                <ZonePolyline/>
                <RefuelMarker/>
                <WarningMarker/>
            </MapComponent>
        </div>
    );
};

OpenStreetMap.propTypes = {
    classes: PropTypes.object,
    onZoomChange: PropTypes.func,
    onCenterChange: PropTypes.func,
    //redux proptypes
    passFitBoundsCallback: PropTypes.func,
    passFitAnyBoundCallback: PropTypes.func,
    manuallyMovedOrZoomed: PropTypes.func,
    selectedCar: PropTypes.object,
    cars: PropTypes.array,
    selectedRoutes: PropTypes.array,
    fitInBounds: PropTypes.func,
    clearRoutes: PropTypes.func,
    clearSelectedCar: PropTypes.func,
    showCurrentRoute: PropTypes.bool,
    currentRoute: PropTypes.object,
    showPoiOnMap: PropTypes.bool,
    mapManuallyMoved: PropTypes.bool,
};

const mapStateToProps = state => {
    return {
        selectedCar: state.mapCarsReducer.selectedCar,
        cars: state.mapCarsReducer.cars,
        selectedRoutes: state.mapRoutesReducer.selectedRoutes,
        showCurrentRoute: state.mapRoutesReducer.showCurrentRoute,
        currentRoute: state.mapRoutesReducer.currentRoute,
        showPoiOnMap: state.filterReducer.showPoiOnMap,
        mapManuallyMoved: state.mapReducer.mapManuallyMoved,
    };
};

const mapDispatchToProps = dispatch => {
    return bindActionCreators(ActionCreator, dispatch);
};

export default compose(
    connect(
        mapStateToProps,
        mapDispatchToProps),
    withStyles(useStyles),
)(OpenStreetMap);

